import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { ArrowRightIcon, CompassIcon, StarIcon, UsersIcon, ShieldCheckIcon } from 'lucide-react';
interface LuxuryToursPageProps {
  onNavigate: (page: string) => void;
}
function ToursHero({
  onNavigate
}: {
  onNavigate: (page: string) => void;
}) {
  return <section className="relative w-full min-h-[50vh] flex items-end overflow-hidden" data-id="element-1735">
      <div className="absolute inset-0" data-id="element-1736">
        <img src="https://images.unsplash.com/photo-1544735716-392fe2489ffa?w=1920&q=80" alt="Himalayan mountain range panoramic view" className="w-full h-full object-cover" data-id="element-1737" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/20" data-id="element-1738" />
      </div>
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-14 pt-32" data-id="element-1739">
        <nav className="mb-5" aria-label="Breadcrumb" data-id="element-1740">
          <ol className="flex items-center gap-2 text-sm text-white/60" data-id="element-1741">
            <li data-id="element-1742">
              <button onClick={() => onNavigate('home')} className="hover:text-white transition-colors" data-id="element-1743">
                Home
              </button>
            </li>
            <li data-id="element-1744">/</li>
            <li className="text-[#D4A853]" data-id="element-1745">Luxury Tours & Treks</li>
          </ol>
        </nav>
        <motion.h1 initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        ease: [0.22, 1, 0.36, 1]
      }} className="font-playfair text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4" data-id="element-1746">
          Luxury Tours &{' '}
          <span className="italic font-medium text-[#D4A853]" data-id="element-1747">Treks</span>
        </motion.h1>
        <motion.p initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.15
      }} className="text-white/70 text-lg max-w-2xl font-light" data-id="element-1748">
          Expertly curated journeys through Nepal's most breathtaking landscapes
          — from Himalayan summits to jungle safaris.
        </motion.p>
      </div>
    </section>;
}
function ToursOverview() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-60px'
  });
  return <section className="w-full bg-[#FAF8F5] py-20 sm:py-28" ref={ref} data-id="element-1749">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1750">
        <motion.div initial={{
        opacity: 0,
        y: 24
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.7
      }} data-id="element-1751">
          <div className="flex items-center gap-3 mb-6" data-id="element-1752">
            <span className="text-[#D4A853]/40 text-xs" data-id="element-1753">◆</span>
            <div className="w-12 h-0.5 bg-[#D4A853]" data-id="element-1754" />
            <span className="text-[#D4A853]/40 text-xs" data-id="element-1755">◆</span>
          </div>
          <h2 className="font-playfair text-3xl sm:text-4xl font-bold text-slate-900 mb-8" data-id="element-1756">
            Luxury Tours & Treks in Nepal
          </h2>
          <div className="space-y-5 text-slate-600 leading-relaxed text-base" data-id="element-1757">
            <p data-id="element-1758">
              Nepal is a land of extraordinary contrasts — a place where ancient
              temples stand in the shadow of the world's tallest mountains,
              where dense subtropical jungles give way to barren high-altitude
              deserts, and where centuries-old traditions coexist with warm,
              modern hospitality. For the discerning traveler, Nepal offers an
              unparalleled canvas of experiences that simply cannot be found
              anywhere else on Earth. From the legendary trails leading to
              Everest Base Camp to the hidden kingdoms of Upper Mustang, every
              journey through this remarkable country reveals new wonders.
            </p>
            <p data-id="element-1759">
              Our luxury tours and treks are designed for travelers who refuse
              to compromise on comfort while seeking authentic adventure. We
              offer a diverse portfolio of experiences: high-altitude trekking
              expeditions through the Himalayas with stays in premium mountain
              lodges; immersive cultural tours through the medieval cities of
              the Kathmandu Valley with private historian guides; wildlife
              safaris in Chitwan and Bardia National Parks from five-star jungle
              lodges; and exclusive helicopter tours that offer breathtaking
              aerial perspectives of peaks like Everest, Annapurna, and
              Machapuchare. Each itinerary is meticulously crafted to balance
              exhilaration with relaxation, ensuring you experience the very
              best of Nepal without the hardships of traditional backpacker
              travel.
            </p>
            <p data-id="element-1760">
              What sets luxury travel in Nepal apart is the attention to every
              detail. On our trekking expeditions, you'll be accompanied by
              personal Sherpa guides who carry your gear while you walk
              unburdened through some of the most dramatic scenery on the
              planet. Evenings are spent in heated lodges with hot showers,
              comfortable beds, and gourmet meals prepared by trained chefs — a
              far cry from the basic teahouses most trekkers endure. For
              cultural tours, we arrange private access to temples and
              monasteries, intimate cooking classes with local families, and
              exclusive encounters with master artisans who keep Nepal's ancient
              crafts alive. Helicopter transfers between destinations eliminate
              long, uncomfortable drives, giving you more time to enjoy each
              location.
            </p>
            <p data-id="element-1761">
              Nepal's trekking seasons peak during autumn (September to
              November) and spring (March to May), when clear skies reveal the
              full majesty of the Himalayan range. Autumn offers the crispest
              mountain views and comfortable temperatures, making it ideal for
              high-altitude treks like Everest Base Camp and the Annapurna
              Circuit. Spring brings rhododendron forests into spectacular
              bloom, painting the hillsides in vivid reds and pinks — a
              photographer's paradise. Winter travel (December to February) is
              perfect for lower-altitude cultural tours and jungle safaris,
              while summer's monsoon season transforms the landscape into a lush
              green wonderland ideal for off-the-beaten-path adventures in
              rain-shadow regions like Upper Mustang and Dolpo.
            </p>
            <p data-id="element-1762">
              At Nepal Luxury Travel, we bring over a decade of expertise to
              every journey we craft. Our team of local travel designers,
              certified mountain guides, and hospitality professionals work
              together to create seamless, unforgettable experiences. We
              maintain direct relationships with Nepal's finest hotels, lodges,
              and airlines, ensuring our guests receive priority access and
              preferential rates. Every trip includes comprehensive safety
              measures — satellite communication devices, emergency evacuation
              plans, and guides trained in high-altitude medicine. Whether
              you're a seasoned adventurer seeking your next summit or a
              first-time visitor dreaming of the Himalayas, we'll design a
              journey that exceeds your expectations in every way.
            </p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-12" data-id="element-1763">
            <div className="bg-white rounded-xl p-5 border border-[#D4A853]/10 text-center" data-id="element-1764">
              <ShieldCheckIcon className="w-6 h-6 text-teal-600 mx-auto mb-2" data-id="element-1765" />
              <p className="text-base font-semibold text-slate-900" data-id="element-1766">
                Safety First
              </p>
              <p className="text-sm text-slate-500 mt-1" data-id="element-1767">
                Certified guides & gear
              </p>
            </div>
            <div className="bg-white rounded-xl p-5 border border-[#D4A853]/10 text-center" data-id="element-1768">
              <StarIcon className="w-6 h-6 text-teal-600 mx-auto mb-2" data-id="element-1769" />
              <p className="text-base font-semibold text-slate-900" data-id="element-1770">
                5-Star Stays
              </p>
              <p className="text-sm text-slate-500 mt-1" data-id="element-1771">
                Premium lodges & hotels
              </p>
            </div>
            <div className="bg-white rounded-xl p-5 border border-[#D4A853]/10 text-center" data-id="element-1772">
              <UsersIcon className="w-6 h-6 text-teal-600 mx-auto mb-2" data-id="element-1773" />
              <p className="text-base font-semibold text-slate-900" data-id="element-1774">
                Small Groups
              </p>
              <p className="text-sm text-slate-500 mt-1" data-id="element-1775">Max 12 travelers</p>
            </div>
            <div className="bg-white rounded-xl p-5 border border-[#D4A853]/10 text-center" data-id="element-1776">
              <CompassIcon className="w-6 h-6 text-teal-600 mx-auto mb-2" data-id="element-1777" />
              <p className="text-base font-semibold text-slate-900" data-id="element-1778">
                Custom Trips
              </p>
              <p className="text-sm text-slate-500 mt-1" data-id="element-1779">
                Fully tailored itineraries
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}
function ToursGrid({
  onNavigate
}: {
  onNavigate: (page: string) => void;
}) {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-60px'
  });
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: 40
    },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.7,
        delay: i * 0.1,
        ease: [0.22, 1, 0.36, 1]
      }
    })
  };
  return <section className="w-full bg-[#0C1F2C] py-20 sm:py-28" ref={ref} data-id="element-1780">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1781">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.7
      }} className="text-center mb-14" data-id="element-1782">
          <div className="flex items-center justify-center gap-3 mb-6" data-id="element-1783">
            <span className="text-[#D4A853]/40 text-xs" data-id="element-1784">◆</span>
            <div className="w-12 h-0.5 bg-[#D4A853]" data-id="element-1785" />
            <span className="text-[#D4A853]/40 text-xs" data-id="element-1786">◆</span>
          </div>
          <h2 className="font-playfair text-3xl sm:text-4xl font-bold text-white mb-3" data-id="element-1787">
            All Luxury Tours & Treks
          </h2>
          <p className="text-slate-400 font-light text-lg" data-id="element-1788">
            Choose your next Himalayan adventure
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-1789">
          <motion.article custom={0} variants={cardVariants} initial="hidden" animate={isInView ? 'visible' : 'hidden'} className="group bg-[#132F3E] rounded-2xl overflow-hidden border border-white/5 cursor-pointer" onClick={() => onNavigate('everest-trek')} data-id="element-1790">
            <div className="relative overflow-hidden aspect-[4/3]" data-id="element-1791">
              <img src="https://images.unsplash.com/photo-1516302752625-fcc3c50ae61f?w=600&q=80" alt="Everest Base Camp Trek" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" data-id="element-1792" />
              <span className="absolute top-4 left-4 px-3 py-1 bg-[#D4A853] text-slate-900 text-xs font-semibold rounded-full" data-id="element-1793">
                14 Days
              </span>
            </div>
            <div className="p-6" data-id="element-1794">
              <h3 className="font-playfair text-xl font-semibold text-white mb-2" data-id="element-1795">
                Everest Base Camp Luxury Trek
              </h3>
              <p className="text-slate-400 text-base leading-relaxed mb-4" data-id="element-1796">
                Trek to the foot of the world's highest peak with luxury lodges,
                gourmet dining, and personal Sherpa guides.
              </p>
              <div className="flex items-center justify-between" data-id="element-1797">
                <span className="text-[#D4A853] font-semibold" data-id="element-1798">
                  From $4,500
                </span>
                <span className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-300 group-hover:text-[#D4A853] transition-colors" data-id="element-1799">
                  View Details <ArrowRightIcon className="w-4 h-4" data-id="element-1800" />
                </span>
              </div>
            </div>
          </motion.article>

          <motion.article custom={1} variants={cardVariants} initial="hidden" animate={isInView ? 'visible' : 'hidden'} className="group bg-[#132F3E] rounded-2xl overflow-hidden border border-white/5" data-id="element-1801">
            <div className="relative overflow-hidden aspect-[4/3]" data-id="element-1802">
              <img src="https://images.unsplash.com/photo-1585409677983-0f6c41ca9c3b?w=600&q=80" alt="Annapurna Circuit" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" data-id="element-1803" />
              <span className="absolute top-4 left-4 px-3 py-1 bg-[#D4A853] text-slate-900 text-xs font-semibold rounded-full" data-id="element-1804">
                12 Days
              </span>
            </div>
            <div className="p-6" data-id="element-1805">
              <h3 className="font-playfair text-xl font-semibold text-white mb-2" data-id="element-1806">
                Annapurna Circuit Premium
              </h3>
              <p className="text-slate-400 text-base leading-relaxed mb-4" data-id="element-1807">
                Circle the Annapurna massif with premium teahouse stays,
                helicopter transfers, and cultural immersion.
              </p>
              <div className="flex items-center justify-between" data-id="element-1808">
                <span className="text-[#D4A853] font-semibold" data-id="element-1809">
                  From $3,800
                </span>
                <span className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-300" data-id="element-1810">
                  View Details <ArrowRightIcon className="w-4 h-4" data-id="element-1811" />
                </span>
              </div>
            </div>
          </motion.article>

          <motion.article custom={2} variants={cardVariants} initial="hidden" animate={isInView ? 'visible' : 'hidden'} className="group bg-[#132F3E] rounded-2xl overflow-hidden border border-white/5" data-id="element-1812">
            <div className="relative overflow-hidden aspect-[4/3]" data-id="element-1813">
              <img src="https://images.unsplash.com/photo-1574950578143-858c6fc139d8?w=600&q=80" alt="Chitwan Safari" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" data-id="element-1814" />
              <span className="absolute top-4 left-4 px-3 py-1 bg-[#D4A853] text-slate-900 text-xs font-semibold rounded-full" data-id="element-1815">
                5 Days
              </span>
            </div>
            <div className="p-6" data-id="element-1816">
              <h3 className="font-playfair text-xl font-semibold text-white mb-2" data-id="element-1817">
                Chitwan Luxury Safari
              </h3>
              <p className="text-slate-400 text-base leading-relaxed mb-4" data-id="element-1818">
                Encounter rhinos, tigers, and exotic birds from the comfort of a
                five-star jungle lodge.
              </p>
              <div className="flex items-center justify-between" data-id="element-1819">
                <span className="text-[#D4A853] font-semibold" data-id="element-1820">
                  From $2,200
                </span>
                <span className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-300" data-id="element-1821">
                  View Details <ArrowRightIcon className="w-4 h-4" data-id="element-1822" />
                </span>
              </div>
            </div>
          </motion.article>

          <motion.article custom={3} variants={cardVariants} initial="hidden" animate={isInView ? 'visible' : 'hidden'} className="group bg-[#132F3E] rounded-2xl overflow-hidden border border-white/5" data-id="element-1823">
            <div className="relative overflow-hidden aspect-[4/3]" data-id="element-1824">
              <img src="https://images.unsplash.com/photo-1503220317375-aaad61436b1b?w=600&q=80" alt="Upper Mustang" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" data-id="element-1825" />
              <span className="absolute top-4 left-4 px-3 py-1 bg-[#D4A853] text-slate-900 text-xs font-semibold rounded-full" data-id="element-1826">
                10 Days
              </span>
            </div>
            <div className="p-6" data-id="element-1827">
              <h3 className="font-playfair text-xl font-semibold text-white mb-2" data-id="element-1828">
                Upper Mustang Expedition
              </h3>
              <p className="text-slate-400 text-base leading-relaxed mb-4" data-id="element-1829">
                Explore the forbidden kingdom of Lo Manthang with exclusive
                access, luxury camping, and ancient monasteries.
              </p>
              <div className="flex items-center justify-between" data-id="element-1830">
                <span className="text-[#D4A853] font-semibold" data-id="element-1831">
                  From $5,200
                </span>
                <span className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-300" data-id="element-1832">
                  View Details <ArrowRightIcon className="w-4 h-4" data-id="element-1833" />
                </span>
              </div>
            </div>
          </motion.article>

          <motion.article custom={4} variants={cardVariants} initial="hidden" animate={isInView ? 'visible' : 'hidden'} className="group bg-[#132F3E] rounded-2xl overflow-hidden border border-white/5" data-id="element-1834">
            <div className="relative overflow-hidden aspect-[4/3]" data-id="element-1835">
              <img src="https://images.unsplash.com/photo-1486911278844-a81c5267e227?w=600&q=80" alt="Langtang Valley Trek" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" data-id="element-1836" />
              <span className="absolute top-4 left-4 px-3 py-1 bg-[#D4A853] text-slate-900 text-xs font-semibold rounded-full" data-id="element-1837">
                8 Days
              </span>
            </div>
            <div className="p-6" data-id="element-1838">
              <h3 className="font-playfair text-xl font-semibold text-white mb-2" data-id="element-1839">
                Langtang Valley Trek
              </h3>
              <p className="text-slate-400 text-base leading-relaxed mb-4" data-id="element-1840">
                Discover the "Valley of Glaciers" with pristine forests, Tamang
                culture, and stunning mountain panoramas.
              </p>
              <div className="flex items-center justify-between" data-id="element-1841">
                <span className="text-[#D4A853] font-semibold" data-id="element-1842">
                  From $2,800
                </span>
                <span className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-300" data-id="element-1843">
                  View Details <ArrowRightIcon className="w-4 h-4" data-id="element-1844" />
                </span>
              </div>
            </div>
          </motion.article>

          <motion.article custom={5} variants={cardVariants} initial="hidden" animate={isInView ? 'visible' : 'hidden'} className="group bg-[#132F3E] rounded-2xl overflow-hidden border border-white/5" data-id="element-1845">
            <div className="relative overflow-hidden aspect-[4/3]" data-id="element-1846">
              <img src="https://images.unsplash.com/photo-1558799401-1dcba79834c2?w=600&q=80" alt="Kathmandu Cultural Tour" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" data-id="element-1847" />
              <span className="absolute top-4 left-4 px-3 py-1 bg-[#D4A853] text-slate-900 text-xs font-semibold rounded-full" data-id="element-1848">
                4 Days
              </span>
            </div>
            <div className="p-6" data-id="element-1849">
              <h3 className="font-playfair text-xl font-semibold text-white mb-2" data-id="element-1850">
                Kathmandu Heritage Tour
              </h3>
              <p className="text-slate-400 text-base leading-relaxed mb-4" data-id="element-1851">
                Explore UNESCO World Heritage sites, ancient temples, and
                vibrant markets with private guides and luxury stays.
              </p>
              <div className="flex items-center justify-between" data-id="element-1852">
                <span className="text-[#D4A853] font-semibold" data-id="element-1853">
                  From $1,500
                </span>
                <span className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-300" data-id="element-1854">
                  View Details <ArrowRightIcon className="w-4 h-4" data-id="element-1855" />
                </span>
              </div>
            </div>
          </motion.article>
        </div>
      </div>
    </section>;
}
export function LuxuryToursPage({
  onNavigate
}: LuxuryToursPageProps) {
  return <div className="w-full" data-id="element-1856">
      <ToursHero onNavigate={onNavigate} data-id="element-1857" />
      <ToursOverview data-id="element-1858" />
      <ToursGrid onNavigate={onNavigate} data-id="element-1859" />
    </div>;
}